import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RentalCart() {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();

    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingOut, setCheckingOut] = useState(false);
    const [delivery, setDelivery] = useState(false); // false = self_pickup, true = paid_delivery
    const [address, setAddress] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (user && user.subscription_status === 'active') {
            navigate('/my-library');
        }
    }, [user, navigate]);

    const fetchCart = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/rentals/cart`, { headers: getAuthHeader() });
            setCart(res.data);
            setDelivery(res.data.delivery_option === 'paid_delivery');
            setAddress(res.data.delivery_address || user?.address || '');
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [getAuthHeader, user]);

    useEffect(() => { fetchCart(); }, [fetchCart]);

    const handleRemove = async (bookId) => {
        try {
            const res = await axios.delete(`${API}/rentals/cart/items/${bookId}`, { headers: getAuthHeader() });
            setCart(res.data);
            window.dispatchEvent(new Event('rental-cart-updated'));
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not remove item');
        }
    };

    const handleCheckout = async () => {
        if (!cart || cart.item_count === 0) return;
        if (delivery && !address.trim()) { setError('Please enter a delivery address.'); return; }
        setError('');
        setCheckingOut(true);
        try {
            await axios.patch(`${API}/rentals/cart`,
                { delivery_option: delivery ? 'paid_delivery' : 'self_pickup', delivery_address: delivery ? address : '' },
                { headers: getAuthHeader() });
            const orderRes = await axios.post(`${API}/rentals/cart/checkout`, {}, { headers: getAuthHeader() });
            const { cart_checkout_id, razorpay_order_id, razorpay_key_id, amount, item_count } = orderRes.data;
            const options = {
                key: razorpay_key_id, amount, currency: 'INR',
                name: 'The Hyderabad Library',
                description: `Rental cart — ${item_count} book${item_count === 1 ? '' : 's'}`,
                order_id: razorpay_order_id,
                handler: async (payResp) => {
                    try {
                        await axios.post(`${API}/rentals/cart/verify-payment`, {
                            cart_checkout_id,
                            razorpay_payment_id: payResp.razorpay_payment_id,
                            razorpay_order_id: payResp.razorpay_order_id,
                            razorpay_signature: payResp.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        window.dispatchEvent(new Event('rental-cart-updated'));
                        navigate('/my-library');
                    } catch (err) {
                        setError(err.response?.data?.detail || 'Payment verification failed.');
                        fetchCart();
                    } finally { setCheckingOut(false); }
                },
                modal: { ondismiss: () => { setCheckingOut(false); fetchCart(); } },
                prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
                theme: { color: '#C0562E' },
            };
            if (window.Razorpay) {
                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', () => { setError('Payment failed. Please try again.'); setCheckingOut(false); fetchCart(); });
                rzp.open();
            } else {
                setError('Razorpay not loaded.');
                setCheckingOut(false);
            }
        } catch (e) {
            setError(e.response?.data?.detail || 'Checkout failed.');
            setCheckingOut(false);
        }
    };

    const items = cart?.items || [];
    const total = items.reduce((s, i) => s + Number(i.price || 0), 0);
    const bookCount = items.length + ' book' + (items.length === 1 ? '' : 's');

    if (loading) {
        return (
            <PublicLayout theme="archive">
                <div style={{ padding: '80px 48px', textAlign: 'center', fontStyle: 'italic', opacity: 0.7 }}>Loading your cart…</div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout theme="archive" grid="--a2" gridStrength={28}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a5) 0 5px, transparent 6px)' }} />
            <div className="hydlib-pad" style={{ maxWidth: 1140, margin: '0 auto', padding: '56px 48px 72px' }}>
                <Link to="/catalog" style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 5 }}>← keep browsing</Link>

                <h1 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 48, margin: '32px 0 8px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>Your rental cart</span>
                </h1>
                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 40px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>₹200 per adult book · ₹100 per children&apos;s book · pay once, no membership needed</span>
                </p>

                {items.length === 0 ? (
                    <div style={{ border: '1px dashed var(--line)', background: 'var(--paper)', padding: '40px 32px', textAlign: 'center' }} data-testid="cart-empty">
                        <p style={{ fontSize: 17, margin: '0 0 20px' }}>Your cart is empty. Head to the catalog to fill it.</p>
                        <Link to="/catalog" className="hydlib-highlight" style={{ display: 'inline-block', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '12px 24px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 58%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 42%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 64%, var(--paper)) 94%, transparent 98.5%)` }}>Browse the catalog →</Link>
                    </div>
                ) : (
                    <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 32, alignItems: 'start' }}>
                        {/* LEFT — items + delivery + guest details */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* Items */}
                            <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '24px 26px' }}>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a2)', marginBottom: 16 }}>Books in your cart · {items.length}</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {items.map((it) => (
                                        <div key={it.book_id} style={{ display: 'flex', gap: 14, alignItems: 'center', borderBottom: '1px dashed var(--line)', paddingBottom: 12 }} data-testid={`cart-item-${it.book_id}`}>
                                            <div style={{ width: 46, height: 68, background: 'var(--bg)', border: '1px solid var(--line)', overflow: 'hidden', flexShrink: 0 }}>
                                                {it.cover_image_url && <img src={it.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 18 }}>{it.title}</div>
                                                {it.authors?.length > 0 && <div style={{ fontSize: 13, fontStyle: 'italic', opacity: 0.85 }}>by {it.authors.join(', ')}</div>}
                                            </div>
                                            <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14 }}>₹{it.price}</div>
                                            <button onClick={() => handleRemove(it.book_id)} data-testid={`cart-remove-${it.book_id}`} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: "'Space Mono', monospace", fontSize: 12, textDecoration: 'underline' }}>Remove</button>
                                        </div>
                                    ))}
                                </div>
                                <Link to="/catalog" data-testid="cart-add-more" style={{ display: 'inline-block', marginTop: 16, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--a3)' }}>+ add more books</Link>
                            </div>

                            {/* Delivery */}
                            <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a5)', background: 'var(--paper)', padding: '28px 26px' }}>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a5)', marginBottom: 16 }}>Pickup or delivery</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <button onClick={() => setDelivery(false)} data-testid="option-pickup" style={{ cursor: 'pointer', textAlign: 'left', border: `2px solid ${!delivery ? 'var(--a5)' : 'var(--line)'}`, background: 'var(--bg)', color: 'var(--ink)', padding: '14px 16px', fontFamily: 'inherit' }}>
                                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1 }}>{!delivery ? '●' : '○'} PICKUP BOX</div>
                                        <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>Free · Mon–Sat</div>
                                    </button>
                                    <button onClick={() => setDelivery(true)} data-testid="option-delivery" style={{ cursor: 'pointer', textAlign: 'left', border: `2px solid ${delivery ? 'var(--a5)' : 'var(--line)'}`, background: 'var(--bg)', color: 'var(--ink)', padding: '14px 16px', fontFamily: 'inherit' }}>
                                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1 }}>{delivery ? '●' : '○'} THURSDAY DELIVERY</div>
                                        <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>By distance · paid after delivery</div>
                                    </button>
                                </div>
                                {delivery && (
                                    <p style={{ fontSize: 14, lineHeight: 1.6, opacity: 0.85, margin: '12px 0 0' }}>Delivery charge depends on your distance from the library and is paid <strong>after</strong> the delivery is done — it&apos;s not part of this payment.</p>
                                )}
                                <p style={{ fontFamily: "'Caveat', cursive", fontSize: 19, fontWeight: 600, color: 'var(--a5)', margin: '12px 0 0', transform: 'rotate(-1.5deg)' }}>the delivery person collects your returns too</p>
                            </div>

                            {/* Address (if delivery) */}
                            {delivery && (
                                <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--paper)', padding: '24px 26px' }}>
                                    <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 12 }}>Delivery address</div>
                                    <input
                                        type="text"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder="Flat, street, area, pincode"
                                        data-testid="cart-address"
                                        style={{ width: '100%', border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 13px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' }}
                                    />
                                </div>
                            )}

                            <p style={{ fontSize: 14, fontStyle: 'italic', opacity: 0.8, margin: 0 }}>
                                Renting a lot? <Link to="/signup" style={{ color: 'var(--a3)' }}>A membership pays for itself at 3 books a month.</Link>
                            </p>
                        </div>

                        {/* RIGHT — sticky summary */}
                        <div style={{ position: 'sticky', top: 24, border: '1px solid var(--line)', borderTop: '6px solid var(--a3)', background: 'var(--paper)', padding: '28px 26px' }}>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a3)', marginBottom: 16 }}>Payment summary</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed var(--line)', paddingBottom: 8 }}>
                                    <span>{bookCount}</span>
                                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13 }}>₹{total}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed var(--line)', paddingBottom: 8 }}>
                                    <span>{delivery ? 'Thursday delivery' : 'Pickup box'}</span>
                                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13 }}>{delivery ? 'By distance · pay after' : 'Free'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Libre Caslon Display', serif", fontSize: 24, marginTop: 6 }}>
                                    <span>Total</span><span>₹{total}</span>
                                </div>
                            </div>
                            <div style={{ fontSize: 13.5, fontStyle: 'italic', opacity: 0.8, margin: '14px 0 18px' }}>
                                21-day rentals · extend once by paying the rental fee again (21 more days) · late fee ₹50/day per book
                            </div>
                            {error && <div style={{ padding: '8px 12px', fontSize: 13, border: '1px solid var(--a4)', background: 'color-mix(in oklch, var(--a4) 15%, transparent)', marginBottom: 12 }} data-testid="cart-error">{error}</div>}
                            <button
                                onClick={handleCheckout}
                                disabled={checkingOut || items.length === 0}
                                data-testid="cart-pay-cta"
                                className="hydlib-highlight"
                                style={{ display: 'block', width: '100%', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', border: 'none', padding: 14, cursor: checkingOut ? 'wait' : 'pointer', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`, opacity: checkingOut ? 0.7 : 1 }}
                            >
                                {checkingOut ? 'Processing…' : `Pay ₹${total} — UPI or card`}
                            </button>
                            <span style={{ position: 'absolute', bottom: -18, right: 24, fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-3deg)', background: 'var(--bg)', padding: '0 8px' }}>happy reading!</span>
                        </div>
                    </div>
                )}
            </div>
        </PublicLayout>
    );
}
